import { useContext, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import * as R from 'ramda';

import { Icon, IconButton, Tooltip } from '@atom/mui';
import { ArrowDropDownIcon, ArrowRightIcon, FolderIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';

import InventoryPortalContext from '../InventoryPortalContext';

import InventoryCategoryTreeOptions from './InventoryCategoryTreeOptions';
import InventoryCategoryTreeRow from './InventoryCategoryTreeRow';

import paneClasses from '../../../styles/left-pane.module.css';
import treeClasses from './inventoryCategoryTree.module.css';

const styles = {
  indentation: {
    paddingLeft: '2rem',
  },
  overflowStyle: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    width: '12rem',
  },
};

const InventoryCategoryTree = () => {
  const {
    categoryTree,
    loading,
    open,
    toggleOpen,
    activeCategory,
    setActiveCategory,
    setActivePath,
    resetSelectedAssets,
  } = useContext(InventoryPortalContext);
  const [overflowing, setOverflowing] = useState<boolean>(false);
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const { offsetWidth, scrollWidth } = container.current;
    setOverflowing(offsetWidth < scrollWidth);
  }, [categoryTree.name]);

  const expanded = open.has(categoryTree.id);

  const arrowDirection = expanded ? ArrowDropDownIcon : ArrowRightIcon;

  const rowStyle = clsx(
    treeClasses.folderRow,
    categoryTree.id === activeCategory.id && treeClasses.selected,
  );

  const renderExpandedArrow = !R.isEmpty(categoryTree.categories);

  const onToggle = (event: any, id: string) => {
    event.stopPropagation();
    toggleOpen(id);
  };

  const onClick = () => {
    setActiveCategory(categoryTree, []);
    setActivePath([]);
    resetSelectedAssets();
  };

  return (
    <div className={paneClasses.leftPane}>
      <div className={rowStyle} onClick={onClick}>
        <div
          ref={container}
          className={treeClasses.folderNameContainer}
          style={renderExpandedArrow ? {} : styles.indentation}
        >
          {renderExpandedArrow && (
            <IconButton onClick={event => onToggle(event, categoryTree.id)}>
              <Icon color={colors.neutral.dim} icon={arrowDirection} />
            </IconButton>
          )}
          <Icon color={colors.neutral.gray} icon={FolderIcon} />
          <div className={treeClasses.rowNameContainer}>
            <div>
              {!overflowing ? (
                categoryTree.name
              ) : (
                <Tooltip
                  lightTooltip
                  title={categoryTree.name}
                  placement="right-start"
                >
                  <p style={styles.overflowStyle}>{categoryTree.name}</p>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
        {/* onClick handler prevents modal clicks from propagating up */}
        <div onClick={event => event.stopPropagation()}>
          <InventoryCategoryTreeOptions
            category={categoryTree}
            path={[]}
            isRootFolder
          />
        </div>
      </div>
      {expanded && !loading ? (
        <>
          {categoryTree.categories.map((child: any, index: number) => (
            <InventoryCategoryTreeRow
              key={child.id}
              category={child}
              indentation={0}
              path={['categories', index]}
            />
          ))}
        </>
      ) : (
        <div />
      )}
    </div>
  );
};

export default InventoryCategoryTree;
